import { useEffect, useState } from 'react';
import { useUtmTracking } from '../hooks/useUtmTracking';
import { fetchBooking, fetchBookingByContact } from '../lib/api';
import { trackEvent, sendHitFunnel } from '../lib/tracking';
import type { BookingData } from '../types';
import { UrgencyBanner } from '../components/shared/UrgencyBanner';
import { Loader } from '../components/shared/Loader';
import { Logo } from '../components/shared/Logo';
import { BookingDetails } from '../components/confirmation/BookingDetails';
import { CalendarButtons } from '../components/confirmation/CalendarButtons';
import { MentorCard } from '../components/confirmation/MentorCard';
import { StepsBeforeSession } from '../components/confirmation/StepsBeforeSession';

function ConfirmationError({ message }: { message: string | null }) {
  return (
    <div className="min-h-screen bg-hit-black flex items-center justify-center p-4">
      <div className="hit-card max-w-sm w-full text-center">
        <p className="text-2xl mb-4">⚠️</p>
        <h2 className="font-bold text-hit-black text-lg mb-2">No se pudo cargar tu confirmación</h2>
        <p className="text-hit-gray-dark text-sm mb-4">
          {message ?? 'No se pudo cargar tu confirmación. Revisa tu email.'}
        </p>
        <p className="text-xs text-hit-gray-mid">
          Si el problema persiste, contacta a soporte por WhatsApp.
        </p>
      </div>
    </div>
  );
}

export default function ConfirmationPage() {
  const params = new URLSearchParams(window.location.search);
  const appointmentId = params.get('appointment_id');
  const contactId = params.get('contact_id');
  const { utmParams } = useUtmTracking();

  const [booking, setBooking] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cita agendada (se llega aquí tras confirmar) → mc-events (hit_mentor).
  useEffect(() => {
    sendHitFunnel('agendo');
  }, []);

  useEffect(() => {
    // GHL no soporta {{appointment.id}} en el redirect URL — solo {{contact.id}}.
    // Priorizamos contact_id (vía /booking/by-contact); appointment_id queda como
    // fallback para tests directos o links manuales con ID conocido.
    const promise =
      contactId && contactId.length > 0
        ? fetchBookingByContact(contactId)
        : appointmentId && appointmentId.length > 0
          ? fetchBooking(appointmentId)
          : null;

    if (!promise) {
      setError('No encontramos tu reserva. Revisa el email de confirmación o contacta a soporte.');
      setLoading(false);
      return;
    }

    promise
      .then(data => {
        setBooking(data);
        setLoading(false);

        const trackingMeta = {
          contact_id: contactId ?? undefined,
          appointment_id: appointmentId ?? undefined,
          ...utmParams,
        };
        // Schedule = reserva CONFIRMADA (esta página solo carga con appointment/contact
        // real). eventID estable para deduplicar cuando el CAPI mande el mismo Schedule.
        const scheduleId = `hit:agendo:${appointmentId ?? contactId ?? ''}`;
        trackEvent('Schedule', trackingMeta, scheduleId);
        // Schedule es el ÚNICO evento de "agendó" (browser + CAPI, deduplicado por
        // eventID). Se sacó CompleteRegistration: disparaba en el MISMO momento que
        // Schedule → confundía al elegir el evento de optimización de las campañas.
        // Purchase tampoco va acá: la venta es post-llamada (pago variable del closer)
        // → el Purchase real sale del checkout vía CAPI (evento pago).
      })
      .catch(err => {
        console.error(err);
        setError('No se pudo cargar tu confirmación. Revisa tu email.');
        setLoading(false);
      });
  }, [appointmentId, contactId]);

  if (loading) return <Loader message="✨ Preparando tu confirmación..." />;
  if (error || !booking) return <ConfirmationError message={error} />;

  const calendarTitle = 'Sesión Estratégica HIT';
  const calendarDescription = `Sesión 1-a-1 con tu mentor ${booking.mentor.name}. Link: ${booking.appointment.meetingLink}`;

  return (
    <div className="min-h-screen bg-hit-black">
      <UrgencyBanner
        icon="🎉"
        message="¡Booking confirmado! Tu lugar en HIT 2026 está reservado."
        variant="success"
      />

      <div className="max-w-xl mx-auto w-full pb-10">
        <div className="pt-6 pb-4 px-4 text-center">
          <Logo />
        </div>

        <div className="px-4 pb-6 text-center">
          <h1 className="text-2xl font-extrabold text-hit-white mb-2">
            🎉 ¡Felicidades, {booking.user.firstName}!
          </h1>
          <p className="text-hit-gray-mid text-sm">
            Tu Sesión Estratégica HIT está agendada.
          </p>
        </div>

        <div className="px-4 flex flex-col gap-4">
          <BookingDetails
            startTime={booking.appointment.startTime}
            endTime={booking.appointment.endTime}
            timezone={booking.appointment.timezone}
            meetingLink={booking.appointment.meetingLink}
          />

          <CalendarButtons
            title={calendarTitle}
            description={calendarDescription}
            startTime={booking.appointment.startTime}
            endTime={booking.appointment.endTime}
            location={booking.appointment.meetingLink}
          />

          <MentorCard
            name={booking.mentor.name}
            whatsapp={booking.mentor.whatsapp}
            userName={booking.user.firstName}
            startTime={booking.appointment.startTime}
            utmParams={utmParams}
          />

          <StepsBeforeSession />
        </div>

        <div className="px-4 pt-6 text-center">
          <p className="text-sm font-bold text-hit-warning">
            🔥 No pierdas tu plaza. Reagendar después de 24h tiene costo.
          </p>
          <p className="text-xs text-hit-gray-dark mt-2">
            ⚠️ Si no asistes, perderás tu lugar en HIT 2026.
          </p>
        </div>
      </div>
    </div>
  );
}
